import { useEffect, useState } from "react";
import { useParams } from 'react-router';
import { containerMiddlewareAdEPortoEnergy } from "../../../../server/constants.api";
import useFetch from "../../../../server/useFetch";
import { useTranslation } from "react-i18next";
import { Row, Col, Flex } from 'antd';
import { Text } from '../../Atomics/Text';
import { alignKeys } from "../../../../utils/hooks/alignKeys";
import { NewWidget } from "../../Atomics/NewWidget";
import { unitConvertSingleValue } from "../../../../utils/hooks/unitConverter";
import { isEmpty } from "../../../../utils/hooks/isEmpty";
import { convertTimestampToDayAndMonth, convertTimestampToDayAndMonthAndYear } from "../../../../utils/hooks/convertTimestamp";

export const PeakLoad = ({ dataToFilter }) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [metricChart, setMetricChart] = useState('Active Power');

  const {
    data: peakActivePower,
    isFetching: peakActivePowerIsFetching,
    refetch: refetchPeakActivePower,
  } = useFetch("peak/active-power", "getPeakActivePower",
    {
      id: id,
      startDate: dataToFilter?.previousPeriod?.toISOString()?.split('.')[0],//Traded because we need the last 31 days
      endDate: dataToFilter?.startDay?.toISOString()?.split('.')[0],//Traded because we need the last 31 days
    },
    null, null, containerMiddlewareAdEPortoEnergy
  );

  const {
    data: peakInductivePower,
    isFetching: peakInductivePowerIsFetching,
    refetch: refetchPeakInductivePower,
  } = useFetch("peak/inductive-power", "getPeakInductivePower",
    {
      id: id,
      startDate: dataToFilter?.previousPeriod?.toISOString()?.split('.')[0],//Traded because we need the last 31 days
      endDate: dataToFilter?.startDay?.toISOString()?.split('.')[0],//Traded because we need the last 31 days
    },
    null, null, containerMiddlewareAdEPortoEnergy
  );

  const {
    data: peakCapacitivePower,
    isFetching: peakCapacitivePowerIsFetching,
    refetch: refetchPeakCapacitivePower,
  } = useFetch("peak/capacitive-power", "getPeakCapacitivePower",
    {
      id: id,
      startDate: dataToFilter?.previousPeriod?.toISOString()?.split('.')[0],//Traded because we need the last 31 days
      endDate: dataToFilter?.startDay?.toISOString()?.split('.')[0],//Traded because we need the last 31 days
    },
    null, null, containerMiddlewareAdEPortoEnergy
  );

  const {
    data: peakProductionPower,
    isFetching: peakProductionPowerIsFetching,
    refetch: refetchPeakProductionPower,
  } = useFetch("peak/production-power", "getPeakProductionPower",
    {
      id: id,
      startDate: dataToFilter?.previousPeriod?.toISOString()?.split('.')[0],//Traded because we need the last 31 days
      endDate: dataToFilter?.startDay?.toISOString()?.split('.')[0],//Traded because we need the last 31 days
    },
    null, null, containerMiddlewareAdEPortoEnergy
  );

  const {
    data: peakBigNumbers,
    isFetching: peakBigNumbersIsFetching,
    refetch: refetchPeakBigNumbers,
  } = useFetch("peak/big-numbers", "getPeakBigNumbers",
    {
      id: id,
      startDate: dataToFilter?.previousPeriod?.toISOString()?.split('.')[0],//Traded because we need the last 31 days
      endDate: dataToFilter?.startDay?.toISOString()?.split('.')[0],//Traded because we need the last 31 days
    },
    null, null, containerMiddlewareAdEPortoEnergy
  );

  useEffect(() => {
    refetchPeakActivePower();
    refetchPeakInductivePower();
    refetchPeakCapacitivePower();
    refetchPeakProductionPower();
    refetchPeakBigNumbers();
  }, [dataToFilter]);

  return (
    <Flex id='Timeline Load Diagrams' className='ant-anchors-content-item' gap={32} vertical="true">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Text $fontColor='coolgray-12' $fontSize='text_xl' $weight='regular' $marginRight='s03'>{t('informationFromPreviousPeriod')}</Text>
        </Col>
        <Col span={4}>
          <Flex gap={16} vertical="true">
            <NewWidget data={{
              background: 'white',
              title: t('highestRegistedPeak'),
              charts: [{
                height: '101px',
                chartType: 'bigNumberChart',
                chartId: 'bigNumberChartOne',
                isFetching: peakActivePowerIsFetching,
                data: peakActivePower?.data.dailyPeak && !isEmpty(peakActivePower?.data.dailyPeak) ? 
                { number: getMaxPeak(peakActivePower?.data.dailyPeak), unit: peakActivePower?.data.unit, icon: 'Bolt' } : null
              }]
            }} />
            <NewWidget data={{
              background: 'white',
              title: t('inductiveConsumption'),
              charts: [{
                height: '101px',
                chartType: 'bigNumberChart',
                chartId: 'bigNumberChartOne',
                isFetching: peakActivePowerIsFetching,
                data: peakBigNumbers?.data.inductiveConsumption ? getBigNumber(peakBigNumbers?.data.inductiveConsumption) : null
              }]
            }} />
          </Flex>
        </Col>
        <Col span={4}>
          <Flex gap={16} vertical="true">
            <NewWidget data={{
              background: 'white',
              title: t('activeConsumption'),
              charts: [{
                height: '101px',
                chartType: 'bigNumberChart',
                chartId: 'bigNumberChartOne',
                isFetching: peakActivePowerIsFetching,
                data: peakBigNumbers?.data.activeConsumption ? getBigNumber(peakBigNumbers?.data.activeConsumption) : null
              }]
            }} />
            <NewWidget data={{
              background: 'white',
              title: t('capacitiveConsumption'),
              charts: [{
                height: '101px',
                chartType: 'bigNumberChart',
                chartId: 'bigNumberChartOne',
                isFetching: peakActivePowerIsFetching,
                data: peakBigNumbers?.data.capacitiveConsumption ? getBigNumber(peakBigNumbers?.data.capacitiveConsumption) : null
              }]
            }} />
          </Flex>
        </Col>
        <Col span={16}>
          <NewWidget data={{
            background: 'white',
            title: t('dailyPowerPeaks'),
            charts: [{
              height: '266px',
              chartType: 'mixedLineColumnChartApex',
              chartId: 'mixBarChartLineThree',
              isFetching: metricChart === "Active Power" ? peakActivePowerIsFetching : metricChart === "Production Power" ? peakProductionPowerIsFetching : metricChart === "Inductive Power" ? peakInductivePowerIsFetching : peakCapacitivePowerIsFetching,
              data: metricChart === "Active Power" ? (peakActivePower?.data ? getActivePower(peakActivePower?.data, t) : null) :
                metricChart === "Production Power" ? (peakProductionPower?.data ? getPowerValues(peakProductionPower?.data, metricChart, t) : null) :
                  metricChart === "Inductive Power" ? (peakInductivePower?.data ? getPowerValues(peakInductivePower?.data, metricChart, t) : null) :
                    metricChart === "Capacitive Power" ? (peakCapacitivePower?.data ? getPowerValues(peakCapacitivePower?.data, metricChart, t) : null) : null
            }],
            buttons: [
              {
                key: "Active Power",
                title: t("Active Power"),
                onButtonClick: (value, key) => setMetricChart(key),
                isActive: metricChart === "Active Power",
              },
              {
                key: "Production Power",
                title: t("Production Power"),
                onButtonClick: (value, key) => setMetricChart(key),
                isActive: metricChart === "Production Power",
              },
              {
                key: "Inductive Power",
                title: t("Inductive Power"),
                onButtonClick: (value, key) => setMetricChart(key),
                isActive: metricChart === "Inductive Power",
              },
              {
                key: "Capacitive Power",
                title: t("Capacitive Power"),
                onButtonClick: (value, key) => setMetricChart(key),
                isActive: metricChart === "Capacitive Power",
              },
            ],
          }} />
        </Col>
      </Row>
    </Flex >
  )
};

const getMaxPeak = (data) => {
  const maxPeak = Object.values(data).reduce((a, b) => Math.max(a, b), 0);
  return maxPeak;
};

const getBigNumber = (data) => {
  const { result, newUnit } = unitConvertSingleValue(data.value, data.unit);
  return { number: result, unit: newUnit, icon: "Bolt" };
};

const getActivePower = (data, t) => {
  const dailyAverageData = convertTimestampToDayAndMonthAndYear(data?.dailyAverage);
  const dailyPeakData = convertTimestampToDayAndMonthAndYear(data?.dailyPeak);

  const { result, keys } = alignKeys('dd/mm/yyyy', { 'Daily Average': dailyAverageData }, { 'Daily Peak': dailyPeakData });
  const series = [];

  result?.map((item, index) => {
    const values = Object.values(item)[0];
    const data = Object.entries(values)?.map(([key, value]) => { return value });

    series.push({
      name: t(Object.keys(item)[0]),//TODO: Add Translations
      type: Object.keys(item)[0] === "Daily Average" ? 'column' : "scatter",
      data: data
    })
  });

  if (series.length > 0) {
    if (keys && data?.contractedPower) {
      const contractedPowerData = keys?.map((item, index) => { return data.contractedPower });

      series.push({
        name: t('Contracted Power'),
        type: 'line',
        data: contractedPowerData
      })
    }

    if (keys && data?.maxAllowablePower) {
      const maxAllowablePowerData = keys?.map((item, index) => { return data.maxAllowablePower });

      series.push({
        name: t('Max Allowed Power'),
        type: 'line',
        data: maxAllowablePowerData
      })
    }
  }

  return {
    "series": series,
    "categories": [],
    "labels": keys,
    "xAxisType": null,
    // "labelFormat": "Monthly", //talves diario faz mais sentido ou horario
    "tooltip": null,
    "unit": "kW",
    "columnWidth": 20,
    "xAxisLabel": null,
    "yAxisLabel": "kW",
    yAxisLabelRotate: -90,
    "color": null,
    tickAmount: 6,
    markersSize: [0, 6],
    // "formatterType": "Monthly", //talves diario faz mais sentido ou horario
    "filename": "Gráfico exportado",
    "headerValue": "valores",
    "headerCategory": "categorias",
    "showTooltipTitle": true,
    xAxisLabelsRotate: 0
  };
};

const getPowerValues = (data, metricName, t) => {
  const series = !isEmpty(data?.data) ? [{
    name: t(metricName),
    type: 'column',
    data: Object.values(data?.data).map(x => x)
  }] : [];
  const keys = Object.keys(data?.data);

  return {
    "series": series,
    "categories": [],
    "labels": keys?.map(x => {
      const date = new Date(Number(x) * 1000);
      const month = date.getMonth() + 1; // Mês de 0 a 11, então soma-se 1
      const day = date.getDate(); // Retorna o dia do mês

      // Formata como 'MM-DD', com dois dígitos para mês e dia
      return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
    }),
    "xAxisType": null,
    // "labelFormat": "Monthly", //talves diario faz mais sentido ou horario
    "tooltip": null,
    "unit": data?.unit,
    "columnWidth": 20,
    "xAxisLabel": null,
    "yAxisLabel": data?.unit,
    yAxisLabelRotate: -90,
    "color": null,
    tickAmount: 6,
    markersSize: [6, 6],
    // "formatterType": "Monthly", //talves diario faz mais sentido ou horario
    "filename": "Gráfico exportado",
    "headerValue": "valores",
    "headerCategory": "categorias",
    "showTooltipTitle": true,
    xAxisLabelsRotate: 0
  };
};